import * as Sentry from "@sentry/react";
import { config } from "./config";

/**
 * Error reporting to Sentry. The build sets VITE_SENTRY_DSN (deploy-account.yml);
 * without it, as in development and tests, nothing is sent.
 *
 * The site shows customers' names, phones and card details, so nothing typed or
 * shown is collected: no request bodies, cookies or query strings (the desktop
 * handoff carries its PKCE state there), no session replay. An error names the
 * signed-in account by its Firebase uid only.
 */
export function initMonitoring(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) {
    return;
  }
  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_SENTRY_RELEASE,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 0.1,
    // Calls to the API carry the trace, so a slow page and the API's work show as one.
    tracePropagationTargets: [config.apiUrl],
    dataCollection: {
      userInfo: false,
      cookies: false,
      httpHeaders: false,
      httpBodies: [],
      urlQueryParams: false,
      stackFrameVariables: false,
    },
  });
}

/** Names the signed-in account (uid only) on errors from here on, or forgets it on sign-out. */
export function setMonitoringUser(uid: string | null): void {
  Sentry.setUser(uid ? { id: uid } : null);
}

export const MonitoringBoundary = Sentry.ErrorBoundary;
